import { useContext, useCallback, useEffect, useReducer, createContext, useRef } from 'react';
import { isNil, isString, noop, isFunction } from 'lodash';

import useEffectSkipFirst from '../../utilities/UseEffectSkipFirst';
import { useSessionStorageJson, useLocalStorageJson } from '../../utilities/StorageUtilities';
import { useThrottled } from '../../utilities/Hooks';

const StateContext = createContext();
const StateDispatchContext = createContext();

// do not expose these directly, we want to be explicit about what gets shared
const useTableState = () => useContext(StateContext);
const useTableStateDispatch = () => useContext(StateDispatchContext);

export const useTableIsInitialState = () => useTableState().isInitialState;
export const useSetTableIsInitialState = makeDispatchPropSetter('isInitialState');

export const useTableSorters = () => useTableState().sorters;
export const useSetTableSorters = makeDispatchPropSetter('sorters');

export const useTableSearch = () => useTableState().search;
export const useSetTableSearch = makeDispatchPropSetter('search');

export const useTableRowBounds = () => useTableState().rowBounds;
export const useSetTableRowBounds = makeDispatchPropSetter('rowBounds');

export const useTableVisibleRows = () => useTableState().visibleRows;
export const useSetTableVisibleRows = makeDispatchPropSetter('visibleRows');

export const useTableColumnOverrides = () => useTableState().columnOverrides;
export const useSetTableColumnOverrides = makeDispatchPropSetter('columnOverrides');

/**
 * Container element which handles all state-related access and changes
 */
export function TableStateProvider(props) {
    const {
        children,
        tableId,
        onStateChanged,
        sorters,
        search,
        state,
        visibleRows,
        enableStickyState,
        columnOverrides: initialColumnOverrides,
    } = props;

    const stateChangedCallbackRef = useRef();
    stateChangedCallbackRef.current = onStateChanged;

    if (!isNil(tableId) && !isString(tableId)) {
        throw new Error(
            `useTableState()- "tableId" configuration must either be null/undefined or a string. Received: "${typeof tableId}" instead`
        );
    }

    const [stickyState, setStickyState] = useStickyState(tableId, enableStickyState);
    const [tableState, dispatch] = useReducer(reducer, null, () =>
        buildInitialState(sorters, search, stickyState, state, visibleRows, initialColumnOverrides)
    );

    // listen for config changes
    useEffectSkipFirst(() => dispatch({ sorters }), [sorters]);
    useEffectSkipFirst(() => dispatch({ search }), [search]);
    useEffectSkipFirst(() => dispatch({ isInitialState: true, ...(state || {}) }), [state]);

    useEffect(() => {
        setStickyState(tableState);
    }, [tableState, setStickyState]);

    // notify the outside world when the state has changed
    useEffect(() => {
        const { current: callback } = stateChangedCallbackRef;

        if (typeof callback === 'function') {
            callback(tableState);
        }
    }, [tableState]);

    return (
        <StateDispatchContext.Provider value={dispatch}>
            <StateContext.Provider value={tableState}>{children}</StateContext.Provider>
        </StateDispatchContext.Provider>
    );
}

export function TableStateConsumer(props) {
    const { children } = props;
    const tableState = useTableState();

    if (typeof children === 'function') {
        return children(tableState);
    }

    return null;
}

function buildInitialState(sorters, search, stickyState, state, visibleRows, columnOverrides) {
    const [firstRow, lastRow] = extractRowBounds(state, visibleRows);

    const rowCount = lastRow - firstRow;
    let calcRowBounds = [firstRow, lastRow];

    if (visibleRows > 0 && rowCount > visibleRows) {
        calcRowBounds = [firstRow, firstRow + visibleRows];
    }

    columnOverrides = columnOverrides || [];
    if (isFunction(columnOverrides)) {
        stickyState = stickyState || {};
        const migrated = columnOverrides(stickyState);
        columnOverrides = migrated.columnOverrides;
        stickyState.columnOverrides = migrated.columnOverrides;
        stickyState.columnOverridesVersion = migrated.columnOverridesVersion;
    }

    return {
        sorters,
        search,
        columnOverrides,

        ...stickyState,
        ...state,

        rowBounds: calcRowBounds,
        isInitialState: true,
    };
}

function extractRowBounds(state, visibleRows) {
    if (state?.rowBounds?.length === 2) {
        return state.rowBounds;
    }

    return [0, visibleRows || 1];
}

function reducer(state, updates) {
    return { ...state, ...updates };
}

function makeDispatchPropSetter(property) {
    return () => {
        const dispatcher = useTableStateDispatch();

        return useCallback((value) => dispatcher({ [property]: value }), [dispatcher]);
    };
}

export function useStickyState(tableId, enableStickyState) {
    // saving to both local storage and session storage;
    // local storage ensures that sticky state persists even when the browser session ends
    // session storage is necessary for pdf reports to work correctly
    const [stickyState, setStickyState] = useLocalStorageJson(`${tableId}-dt-state`);
    const [sessionState, setSessionState] = useSessionStorageJson(`${tableId}-dt-state`);
    const setStickyStateFromTableState = useCallback(
        (tableState) => {
            const { sorters, columnOverrides, columnOverridesVersion } = tableState;
            setStickyState({
                sorters,
                columnOverrides,
                columnOverridesVersion,
            });
            setSessionState({
                sorters,
                columnOverrides,
                columnOverridesVersion,
            });
        },
        [setStickyState, setSessionState]
    );
    const setStickyStateThrottled = useThrottled(setStickyStateFromTableState, 500);

    // despite not using this hook if tableId is invalid or enableStickyState is false,
    // you can't have conditional hooks
    if (typeof tableId !== 'string' || !tableId || !enableStickyState) {
        return [null, noop];
    }

    return [stickyState || sessionState, setStickyStateThrottled];
}
